import { ReactNode } from "react";
import { Dropdown } from "@/components";
import { Delete, Ellipsis, Schedule } from "@/icons";
import { useNavigateToItem } from "@/hooks";
import { useTranslate } from "@/providers/context";

import { CalendarEvent } from "../util.tsx";
import { DropdownOption } from "@/components/Forms/Dropdown.tsx";

const DropdownButton = ({
  options,
  idx,
}: {
  options: DropdownOption[];
  idx: number;
}) => (
  <div>
    {options.length > 0 && (
      <Dropdown
        dataTestId={`calendar-dropdown-${idx}`}
        contentProps={{ align: "end" }}
        options={options}
        optionsClassName="capitalize"
      >
        <Ellipsis
          className="h-6 w-6"
          data-testid={`calendar-dropdown-btn-${idx}`}
        />
      </Dropdown>
    )}
  </div>
);

interface CalendarDrawerItemProps {
  event: CalendarEvent;
  title: string;
  thumbnail: ReactNode;
  icon: ReactNode;
  time: string | null;
  index: number;
  isCompactView: boolean;
  onScheduleAgain?: () => void;
  onDelete?: () => void;
  handleClick?: () => void;
}

export const CalendarDrawerItem = ({
  event: e,
  thumbnail,
  title,
  icon,
  time,
  index: idx,
  isCompactView,
  onScheduleAgain,
  onDelete,
  handleClick,
}: CalendarDrawerItemProps) => {
  const { navigateToItem } = useNavigateToItem();
  const { translate } = useTranslate();

  const dropdownOptions: DropdownOption[] = [];
  if (onScheduleAgain) {
    dropdownOptions.push({
      text: translate("Schedule again"),
      icon: <Schedule className="h-4 w-4" />,
      onClick: onScheduleAgain,
    });
  }
  if (onDelete) {
    dropdownOptions.push({
      text: translate("Delete"),
      color: "error",
      icon: <Delete className="h-4 w-4" />,
      onClick: onDelete,
    });
  }

  const handleThumbnailClick = () => {
    if (handleClick) {
      handleClick();
    } else {
      navigateToItem({
        item: { ChildId: e.ItemId, Type: e.ItemType },
        parentId: e.ItemParentId ? e.ItemParentId : undefined,
      });
    }
  };

  if (isCompactView) {
    return (
      <div className="mb-7 flex items-center gap-4">
        <div
          className="w-[162px] shrink-0 cursor-pointer"
          onClick={handleThumbnailClick}
        >
          {thumbnail}
        </div>
        <div>
          <div className="p2-medium line-clamp-2">{title}</div>
          <div className="p2-medium flex items-center text-item-secondary">
            <div className="mr-2">{icon}</div>
            {time}
          </div>
        </div>
        <div className="ml-auto">
          <DropdownButton options={dropdownOptions} idx={idx} />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10 flex flex-col gap-3 text-item-primary">
      <div className="cursor-pointer" onClick={handleThumbnailClick}>
        {thumbnail}
      </div>
      <div className="p2-medium">{title}</div>
      <div className="flex flex-row items-center justify-between text-item-secondary">
        <div className="flex items-center">
          <div className="mr-2">{icon}</div>
          {time}
        </div>
        <DropdownButton options={dropdownOptions} idx={idx} />
      </div>
    </div>
  );
};
